import axios from "axios";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosRequestConfig, AxiosRequestHeaders } from "axios";
import { useDispatch } from "react-redux";
import { alterUserInfo } from "../app/userInfo";

const instance = axios.create({
  // Trailing slash + absolute "/path" produced "//path" on api.statdoctor.app
  baseURL: (process.env.REACT_APP_URL || "").replace(/\/+$/, ""),
});

let isForcedLogoutInProgress = false;

/** Backend auth/session failure codes returned in `{ code }` response bodies. */
export const AUTH_ERROR_CODES = new Set([
  90000, 90001, 90002, 90003, 90004, 90005, 90006,
]);

export const isAuthErrorCode = (code: unknown): boolean =>
  typeof code === "number" && AUTH_ERROR_CODES.has(code);

/** Join API base + path without producing `//` segments. */
export const joinApiUrl = (base: string, path: string): string => {
  const normalizedBase = (base || "").replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

export const clearAuthSession = () => {
  sessionStorage.clear();
  localStorage.clear();
};

export const resolveActiveHospitalId = (): string | undefined => {
  try {
    const raw = localStorage.getItem("current-hospital");
    if (raw) {
      const hospital = JSON.parse(raw);
      if (hospital?.id) {
        return String(hospital.id);
      }
    }

    const infoRaw = localStorage.getItem("current-hospital-info");
    if (infoRaw) {
      const info = JSON.parse(infoRaw);
      if (info?.id) {
        return String(info.id);
      }
    }

    const selectedId = String(
      localStorage.getItem("selected-hospital-id") || "",
    ).trim();
    if (selectedId) {
      return selectedId;
    }

    const orgRaw = localStorage.getItem("org-session-profile");
    if (orgRaw) {
      const org = JSON.parse(orgRaw);
      if (org?.homeHospitalId) {
        return String(org.homeHospitalId);
      }
      const firstAccessible = org?.accessibleHospitals?.[0]?.id;
      if (firstAccessible) {
        return String(firstAccessible);
      }
    }

    const listRaw = localStorage.getItem("hospitals-list");
    if (listRaw) {
      const list = JSON.parse(listRaw);
      if (Array.isArray(list) && list[0]?.id) {
        return String(list[0].id);
      }
    }
  } catch {}
  return undefined;
};

export interface HospitalRequestAuth {
  /** Value for the `doctor-admin-token` header. */
  token: string;
  /** Value for the `accountId` header. */
  accountId: string;
  /** True when the master token is being used for a multi-hospital request. */
  useMasterToken: boolean;
}

/**
 * Centralized token/account selection for hospital data requests.
 *
 * Multi-hospital requests (e.g. the master dropdown badges) use the master
 * token + the persistent parent hospital as accountId to bypass child-scope
 * locks; single-hospital requests use the child `token-name`. Previously this
 * logic was duplicated across the notifications hook and the hospitals API.
 */
export const resolveHospitalRequestAuth = (
  hospitalIds: string[] = [],
  userId?: string | null,
): HospitalRequestAuth => {
  let masterToken = localStorage.getItem("master-token");
  if (masterToken === "null") masterToken = null;
  let normalToken = localStorage.getItem("token-name");
  if (normalToken === "null") normalToken = null;

  const useMasterToken = !!masterToken && hospitalIds.length > 1;

  let accountId = userId ?? hospitalIds[0] ?? "";
  if (useMasterToken) {
    try {
      const rawParent = localStorage.getItem("persistent-parent-hospital");
      if (rawParent && rawParent !== "null") {
        const parent = JSON.parse(rawParent);
        if (parent?.id) accountId = parent.id;
      }
    } catch {
      // ignore malformed cache; fall back to userId / first hospital
    }
  }

  return {
    token: (useMasterToken ? masterToken : normalToken) || "",
    accountId: String(accountId || ""),
    useMasterToken,
  };
};

instance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const token = localStorage.getItem("token-name");
    const headers = config.headers as AxiosRequestHeaders;
    // Do not clobber an explicitly provided admin token (e.g. master-token for hierarchy assistant).
    if (token && !headers["doctor-admin-token"]) {
      headers["doctor-admin-token"] = token;
    }
    headers["Accept-Language"] = "en-US";
    const hospitalId = resolveActiveHospitalId();
    if (hospitalId && !headers["X-Hospital-Id"]) {
      headers["X-Hospital-Id"] = hospitalId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const shouldBypassForcedLogout = (requestUrl: string) => {
  if (!requestUrl) return false;

  return (
    requestUrl.includes("account-token-generator") ||
    requestUrl.includes("account-info-get") ||
    requestUrl.includes("/notifications")
  );
};

const isInvalidCustomTokenPayload = (payload: any) => {
  const directMessage = String(payload?.message || "").toUpperCase();
  const nestedMessage = String(payload?.error?.message || "").toUpperCase();
  const nestedReason = String(payload?.error?.errors?.[0]?.reason || "").toUpperCase();

  return (
    directMessage.includes("INVALID_CUSTOM_TOKEN") ||
    nestedMessage.includes("INVALID_CUSTOM_TOKEN") ||
    nestedReason.includes("INVALID")
  );
};

const AxiosInterceptor = ({ children }: any) => {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  useEffect(() => {
    const forceLogout = (reason?: string) => {
      if (isForcedLogoutInProgress) {
        return;
      }

      isForcedLogoutInProgress = true;
      console.warn("🔐 Forced logout triggered:", reason || "auth_error");

      clearAuthSession();
      dispatch(alterUserInfo({ loginStatus: false }));
      navigate("/login");

      setTimeout(() => {
        isForcedLogoutInProgress = false;
      }, 1200);
    };

    const resInterceptor = (response: any) => {
      let code = response.data.code;
      // Check if this is a token-related error
      if (isAuthErrorCode(code)) {
        const requestUrl = response.config?.url || "";
        // Requests that are expected to occasionally return 9000x for specific context
        // should be handled by their own callers and not force a global logout.
        if (shouldBypassForcedLogout(requestUrl)) {
          console.warn(
            "🔑 Token generator or info fetch failed - session may have expired. Passing back to caller.",
          );
          return response;
        }

        // For other requests, perform logout
        console.log("🔐 Auth error detected, logging out. Code:", code);
        forceLogout(`code_${code}`);

        // Reject explicitly so callers don't receive undefined response objects.
        return Promise.reject({
          ...response,
          isAuthError: true,
          authCode: code,
        });
      } else {
        return response;
      }
    };

    const errInterceptor = (error: any) => {
      const status = Number(error?.response?.status || 0);
      const payload = error?.response?.data;
      const requestUrl = String(error?.config?.url || "");

      // Firebase custom token errors can surface as HTTP 400 payloads.
      if (status === 400 && isInvalidCustomTokenPayload(payload)) {
        forceLogout("invalid_custom_token");
      }

      // Fallback hard auth failures from backend transport layer.
      if ((status === 401 || status === 403) && !shouldBypassForcedLogout(requestUrl)) {
        forceLogout(`http_${status}`);
      }

      return Promise.reject(error);
    };

    const interceptor = instance.interceptors.response.use(
      resInterceptor,
      errInterceptor,
    );

    return () => instance.interceptors.response.eject(interceptor);
  }, [dispatch, navigate]);

  return children;
};

export default instance;
export { AxiosInterceptor };
